import { createEffect, onCleanup, Show } from "solid-js";
import * as d3 from "d3";
import type { PerformanceDriversFit } from "../../utils/performanceDriversModelFit";
import {
  getBoatQualityBadge,
  getPerformanceDriversConfidenceBadge,
  getSegmentQualityBadge,
  getTestSegmentQualityBadge,
  resolvePerformanceDriversTestRmse,
} from "../../utils/performanceDriversModelFit";
import {
  performanceDriversModelFitExplanation,
  performanceDriversModelFitTitle,
  performanceDriversParityTitle,
} from "../../utils/performanceDriversExplanation";

interface PerformanceDriversModelFitProps {
  fit: PerformanceDriversFit | null | undefined;
  nSamples: number;
  nBoats: number;
  skipped?: boolean;
  skipReason?: string;
  targetUnit?: string;
  title?: string;
  layout?: "stacked" | "horizontal";
  showExplanation?: boolean;
}

const PARITY_HEIGHT_PX = 300;
const PARITY_HEIGHT_HORIZONTAL_PX = 240;

export default function PerformanceDriversModelFit(props: PerformanceDriversModelFitProps) {
  let chartRef: HTMLDivElement | undefined;
  let svgRef: SVGSVGElement | undefined;

  const targetUnit = () => props.targetUnit ?? "VMG%";

  const isHorizontal = () => props.layout === "horizontal";
  const parityHeight = () =>
    isHorizontal() ? PARITY_HEIGHT_HORIZONTAL_PX : PARITY_HEIGHT_PX;

  createEffect(() => {
    const fit = props.fit;
    if (!svgRef || !chartRef || !fit?.parity_points?.length) return;

    const width = Math.min(chartRef.clientWidth || 576, isHorizontal() ? 420 : 576);
    const height = parityHeight();

    const svg = d3.select(svgRef);
    svg.selectAll("*").remove();

    const margin = { top: 12, right: 12, bottom: 44, left: 52 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const points = fit.parity_points;
    const allPredicted = points.map((p) => p.predicted);
    const allActual = points.map((p) => p.actual);
    const extent = d3.extent([...allPredicted, ...allActual]) as [number, number];
    const pad = (extent[1] - extent[0]) * 0.05 || 1;
    const domain: [number, number] = [extent[0] - pad, extent[1] + pad];

    const xScale = d3.scaleLinear().domain(domain).range([0, innerW]);
    const yScale = d3.scaleLinear().domain(domain).range([innerH, 0]);

    const g = svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    g.append("g")
      .attr("class", "performance-drivers-parity-grid performance-drivers-parity-grid-y")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickSizeInner(-innerW)
          .tickSizeOuter(0)
          .tickFormat(() => "")
      );

    g.append("g")
      .attr("class", "performance-drivers-parity-grid performance-drivers-parity-grid-x")
      .attr("transform", `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(5)
          .tickSizeInner(-innerH)
          .tickSizeOuter(0)
          .tickFormat(() => "")
      );

    g.selectAll(".performance-drivers-parity-grid line")
      .attr("class", "performance-drivers-parity-grid-line");

    g.append("line")
      .attr("x1", xScale(domain[0]))
      .attr("y1", yScale(domain[0]))
      .attr("x2", xScale(domain[1]))
      .attr("y2", yScale(domain[1]))
      .attr("class", "performance-drivers-parity-line");

    g.selectAll("circle.performance-drivers-parity-point")
      .data(points)
      .enter()
      .append("circle")
      .attr("class", "performance-drivers-parity-point")
      .attr("cx", (d) => xScale(d.predicted))
      .attr("cy", (d) => yScale(d.actual))
      .attr("r", 3);

    g.append("g")
      .attr("class", "performance-drivers-parity-axis")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(xScale).ticks(5))
      .call((axisG) => axisG.select(".domain").remove())
      .append("text")
      .attr("class", "performance-drivers-parity-axis-title")
      .attr("x", innerW / 2)
      .attr("y", 34)
      .attr("text-anchor", "middle")
      .text(`Predicted ${targetUnit()}`);

    g.append("g")
      .attr("class", "performance-drivers-parity-axis")
      .call(d3.axisLeft(yScale).ticks(5))
      .call((axisG) => axisG.select(".domain").remove())
      .append("text")
      .attr("class", "performance-drivers-parity-axis-title")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerH / 2)
      .attr("y", -40)
      .attr("text-anchor", "middle")
      .text(`Actual ${targetUnit()}`);
  });

  onCleanup(() => {
    if (svgRef) d3.select(svgRef).selectAll("*").remove();
  });

  const cellForBadges = () => ({
    n_samples: props.nSamples,
    n_boats: props.nBoats,
    skipped: props.skipped ?? false,
    skip_reason: props.skipReason,
    fit: props.fit ?? null,
  });

  const confidenceBadge = () => getPerformanceDriversConfidenceBadge(cellForBadges());
  const holdoutRmse = () => resolvePerformanceDriversTestRmse(props.fit ?? null) ?? 0;
  const modelSampleCount = () => props.fit?.model_n_samples ?? props.nSamples;
  const segmentBadge = () => getSegmentQualityBadge(modelSampleCount());
  const boatBadge = () => getBoatQualityBadge(props.nBoats);
  const testCount = () => props.fit?.parity_points?.length ?? 0;
  const testPct = () => (modelSampleCount() > 0 ? Math.round((testCount() / modelSampleCount()) * 100) : 0);
  const testSegmentBadge = () => getTestSegmentQualityBadge(testCount());

  const fitStatsTable = () => {
    const compact = isHorizontal();

    const statusBadge = (
      level: string,
      label: string,
      kind: "trust" | "quality"
    ) => (
      <span
        class={`performance-drivers-stat-badge performance-drivers-${kind}-${level}`}
      >
        {label.toUpperCase()}
      </span>
    );

    return (
      <table
        class={`performance-drivers-fit-stats-table${
          compact ? " performance-drivers-fit-stats-table--compact" : ""
        }`}
      >
        <Show when={!compact}>
          <thead>
            <tr>
              <th scope="col">Metric</th>
              <th scope="col">Value</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
        </Show>
        <tbody>
          <tr title={confidenceBadge().detail}>
            <th scope="row">{compact ? "RMSE" : "Holdout RMSE"}</th>
            <td>
              <span class="performance-drivers-fit-value-cell">
                <span class="performance-drivers-fit-value">
                  {holdoutRmse().toFixed(1)}
                </span>
                <Show when={compact}>
                  {statusBadge(
                    confidenceBadge().level,
                    confidenceBadge().label,
                    "trust"
                  )}
                </Show>
              </span>
            </td>
            <Show when={!compact}>
              <td>
                {statusBadge(
                  confidenceBadge().level,
                  confidenceBadge().label,
                  "trust"
                )}
              </td>
            </Show>
          </tr>
          <tr>
            <th scope="row">{compact ? "Segments" : "Model segments"}</th>
            <td>
              <span class="performance-drivers-fit-value-cell">
                <span class="performance-drivers-fit-value">{modelSampleCount()}</span>
                <Show when={compact}>
                  {statusBadge(segmentBadge().level, segmentBadge().label, "quality")}
                </Show>
              </span>
            </td>
            <Show when={!compact}>
              <td>
                {statusBadge(segmentBadge().level, segmentBadge().label, "quality")}
              </td>
            </Show>
          </tr>
          <tr>
            <th scope="row">Boats</th>
            <td>
              <span class="performance-drivers-fit-value-cell">
                <span class="performance-drivers-fit-value">{props.nBoats}</span>
                <Show when={compact}>
                  {statusBadge(boatBadge().level, boatBadge().label, "quality")}
                </Show>
              </span>
            </td>
            <Show when={!compact}>
              <td>
                {statusBadge(boatBadge().level, boatBadge().label, "quality")}
              </td>
            </Show>
          </tr>
          <tr>
            <th scope="row">{compact ? "Test" : "Test segments"}</th>
            <td>
              <span class="performance-drivers-fit-value-cell">
                <span class="performance-drivers-fit-value">
                  {testCount()} ({testPct()}%)
                </span>
                <Show when={compact}>
                  {statusBadge(
                    testSegmentBadge().level,
                    testSegmentBadge().label,
                    "quality"
                  )}
                </Show>
              </span>
            </td>
            <Show when={!compact}>
              <td>
                {statusBadge(
                  testSegmentBadge().level,
                  testSegmentBadge().label,
                  "quality"
                )}
              </td>
            </Show>
          </tr>
        </tbody>
      </table>
    );
  };

  const parityChart = () => (
    <div class="performance-drivers-parity-panel">
      <Show when={!isHorizontal()}>
        <h4 class="performance-drivers-parity-title">{performanceDriversParityTitle()}</h4>
      </Show>
      <div class="performance-drivers-parity-wrap" ref={chartRef}>
        <svg ref={svgRef} class="performance-drivers-parity-svg" role="img" aria-label="Holdout parity chart" />
      </div>
    </div>
  );

  return (
    <div
      class={`performance-drivers-model-fit${
        isHorizontal() ? " performance-drivers-model-fit--horizontal" : ""
      }`}
    >
      <h3 class="performance-drivers-section-title">
        {props.title ?? performanceDriversModelFitTitle()}
      </h3>
      <Show when={props.showExplanation !== false}>
        <p class="performance-drivers-section-explanation">{performanceDriversModelFitExplanation()}</p>
      </Show>

      <Show
        when={!props.skipped && props.fit}
        fallback={<p class="performance-drivers-empty">{props.skipReason ?? "No model fit for this cell."}</p>}
      >
        <Show
          when={isHorizontal()}
          fallback={
            <>
              {fitStatsTable()}
              {parityChart()}
            </>
          }
        >
          <div class="performance-drivers-model-fit-body">
            {fitStatsTable()}
            {parityChart()}
          </div>
        </Show>
      </Show>
    </div>
  );
}
